import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  TextareaAutosize,
  Grid,
  Paper,
  IconButton,
  Snackbar,
  Alert,
  useMediaQuery,
  useTheme,

} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import axios from "axios";

// Helper for file->Base64
const toBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result.split(",")[1]);
    reader.onerror = reject;
  });

const getUser = () => {
  try {
    return JSON.parse(localStorage.getItem("user")) || {};
  } catch {
    return {};
  }
};

const SmsUiUploadForm = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [video, setVideo] = useState(null);
  const [pdf, setPdf] = useState(null);
  const [images, setImages] = useState([]);
  const [whatsappBalance, setWhatsappBalance] = useState(null);

  const [numbersText, setNumbersText] = useState("");
  const [correctNumbers, setCorrectNumbers] = useState([]);
  const [incorrectNumbers, setIncorrectNumbers] = useState([]);
  const [formatInvalidNumbers, setFormatInvalidNumbers] = useState([]);

  const [message, setMessage] = useState("");
  const [callButtonText, setCallButtonText] = useState("");
  const [callButtonNumber, setCallButtonNumber] = useState("");
  const [linkButtonText, setLinkButtonText] = useState("");
  const [linkButtonUrl, setLinkButtonUrl] = useState("");

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    const fetchBalance = async () => {
      try {
        // Get username from localStorage
        const storedUser = localStorage.getItem("user");
        const parsedUser = storedUser ? JSON.parse(storedUser) : null;
        const username = parsedUser?.username || parsedUser?.email || ""; // fallback empty

        // Make API call with body containing username
        const response = await axios.post(
          "https://d3ioa5kdb9.execute-api.ap-south-1.amazonaws.com/Tis_whatsapp_be/credit_user",
          {
            username: username, // pass in body
          }
        );

        const body =
          response.data?.["body-json"]?.body || response.data?.body || {};
        const whatsappBal = body?.Items?.[0]?.whatsapp_button_balance || 0;
        setWhatsappBalance(whatsappBal);
      } catch (error) {
        console.error("Failed to fetch WhatsApp balance:", error);
      }
    };

    fetchBalance();
  }, []);

  // Handle video upload with size validation
  const handleVideoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // 3MB = 3 * 1024 * 1024 bytes
    const maxSize = 3 * 1024 * 1024;
    if (file.size > maxSize) {
      showSnackbar("Video size must be less than 3MB", "error");
      return;
    }

    setVideo(file);
  };

  // Handle PDF upload with size validation (1MB)
  const handlePdfChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const maxSize = 1 * 1024 * 1024;
    if (file.size > maxSize) {
      showSnackbar("PDF size must be less than 1MB", "error");
      return;
    }

    setPdf(file);
  };

  // Handle message input with character limit
  const handleMessageChange = (e) => {
    const value = e.target.value;
    if (value.length <= 1500) {
      setMessage(value);
    }
  };

  // ✅ FIXED: Enhanced number validation - properly handle 11-digit numbers
  const validateNumbers = async () => {
    const originalNumbers = numbersText
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean);

    if (!originalNumbers.length) {
      showSnackbar("Enter at least one number for validation", "error");
      return;
    }

    const formatInvalidNumbers = [];
    const validFormatNumbers = [];
    const elevenDigitNumbers = [];

    originalNumbers.forEach((num) => {
      let cleaned = num.trim().replace(/[\s\-\(\)\.]/g, "");

      // Remove country codes only if they result in exactly 10 digits
      if (cleaned.startsWith("+91") && cleaned.length === 13) {
        cleaned = cleaned.substring(3);
      } else if (cleaned.startsWith("91") && cleaned.length === 12) {
        cleaned = cleaned.substring(2);
      }

      // Check for 11-digit numbers
      if (/^\d{11}$/.test(cleaned)) {
        elevenDigitNumbers.push(num);
      }
      // Check for valid 10-digit numbers
      else if (/^\d{10}$/.test(cleaned)) {
        validFormatNumbers.push(cleaned);
      } else {
        formatInvalidNumbers.push(num);
      }
    });

    // Set format invalid numbers (includes 11-digit numbers and other invalid formats)
    setFormatInvalidNumbers([...formatInvalidNumbers, ...elevenDigitNumbers]);

    if (validFormatNumbers.length === 0) {
      showSnackbar(
        "No valid 10-digit numbers found. Please check your numbers.",
        "warning"
      );
      setCorrectNumbers([]);
      setIncorrectNumbers([...elevenDigitNumbers, ...formatInvalidNumbers]);
      setNumbersText("");
      setIsSubmitting(false);
      return;
    }

    // Show info message if 11-digit numbers are found
    if (elevenDigitNumbers.length > 0) {
      showSnackbar(
        `${elevenDigitNumbers.length} 11-digit numbers detected and moved to incorrect section.`,
        "info"
      );
    }

    // Call API for valid 10-digit numbers
    try {
      setIsSubmitting(true);
      const resp = await axios.post(
        "https://d3ioa5kdb9.execute-api.ap-south-1.amazonaws.com/Tis_whatsapp_be/number_correct",
        { number: validFormatNumbers }
      );
      const body = resp.data?.body || resp.data?.["body-json"]?.body || {};
      const correctNums = body["new correct Number"] || [];
      const incorrectNums = body["error Number"] || [];

      // ✅ FIXED: Set correct and incorrect numbers properly
      setCorrectNumbers(correctNums);
      // Only API incorrect numbers go here, 11-digit numbers stay in formatInvalidNumbers
      setIncorrectNumbers(incorrectNums);

      // Auto-remove incorrect numbers from input field
      // Keep only the correct numbers in the textarea
      setNumbersText(correctNums.join("\n"));

      // ✅ FIXED: Success message with accurate counts
      const totalValid = correctNums.length;
      const totalInvalid =
        incorrectNums.length +
        elevenDigitNumbers.length +
        formatInvalidNumbers.length;

      if (totalInvalid > 0) {
        showSnackbar(
          `Numbers validated! ${totalValid} valid, ${totalInvalid} invalid.`,
          totalValid > 0 ? "success" : "warning"
        );
      } else {
        showSnackbar(
          `All ${totalValid} numbers validated successfully!`,
          "success"
        );
      }
    } catch {
      showSnackbar("Validation failed", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  // ✅ FIXED: Handle image upload with strict 1-image limit and proper error for 2+ images
  const handleImageChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // Check if user is trying to upload more than 1 image
    if (files.length > 1) {
      showSnackbar("Maximum 1 image allowed. Please select only one image.", "error");
      e.target.value = ""; // Clear the input
      return;
    }

    // If already have 1 image, show error and return immediately
    if (images.length >= 1) {
      showSnackbar("Maximum 1 image allowed. Remove the current image to upload a new one.", "error");
      e.target.value = ""; // Clear the input
      return;
    }

    // Take only the first file (since we only allow 1 image)
    const file = files[0];

    // Check file size (max 1MB)
    const maxSize = 1 * 1024 * 1024; // 1MB in bytes
    if (file.size > maxSize) {
      showSnackbar(
        `Image "${file.name}" is too large. Maximum size is 1MB.`,
        "error"
      );
      e.target.value = ""; // Clear the input
      return;
    }

    // Add the single image
    setImages([file]);
    showSnackbar("Image uploaded successfully!", "success");

    // Reset file input
    e.target.value = '';
  };

  // ✅ FIXED: Handle drag and drop with same 1-image restriction
  const handleImageDrop = (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files).filter((f) =>
      f.type.startsWith("image/")
    );

    if (files.length === 0) return;

    // Check if user is trying to drop more than 1 image
    if (files.length > 1) {
      showSnackbar("Maximum 1 image allowed. Please drop only one image.", "error");
      return;
    }

    // If already have 1 image, show error and return
    if (images.length >= 1) {
      showSnackbar("Maximum 1 image allowed. Remove the current image to upload a new one.", "error");
      return;
    }

    // Take only the first file
    const file = files[0];

    // Check file size (max 1MB)
    const maxSize = 1 * 1024 * 1024; // 1MB in bytes
    if (file.size > maxSize) {
      showSnackbar(
        `Image "${file.name}" is too large. Maximum size is 1MB.`,
        "error"
      );
      return;
    }

    // Add the single image
    setImages([file]);
    showSnackbar("Image uploaded successfully!", "success");
  };

  const handleImageRemove = (i) => {
    setImages((prev) => prev.filter((_, idx) => idx !== i));
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  // SUBMIT FORM - UPDATED WITH AUTO-REFRESH
  const handleSubmit = async () => {
    if (!correctNumbers.length) {
      showSnackbar("Validate at least one correct number", "error");
      return;
    }
    if (!message) {
      showSnackbar("Please enter a message", "error");
      return;
    }

    setIsSubmitting(true);
    const user = getUser();
    const username = user.username || user.email || "";

    try {
      const imagedata = await Promise.all(
        images.map(async (file) => ({
          data_url: await toBase64(file),
          file: { name: file.name },
        }))
      );

      const payload = {
        remark: "wappb campaign",
        transaction_user: username,
        username,
        count: correctNumbers.length.toString(),
        type: "whatsapp_button_balance",
        imagedata,
        pdf_name: pdf?.name || null,
        pdf: pdf ? await toBase64(pdf) : null,
        video_name: video?.name || null,
        video: video ? await toBase64(video) : null,
        buttonNameList: {
          buttonNameaFromChild: [callButtonText, linkButtonText],
        },
        buttonDataList: {
          buttonDataFromChild: [callButtonNumber, linkButtonUrl],
        },
        waButtonName: null,
        waButtonNumber: null,
        waButtonMsg: null,
        content: {
          id: "y4HNf5ap",
          name: "CMPy4HNf5ap",
          phone_numbers: correctNumbers,
          message,
        },
      };

      await axios.post(
        "https://d3ioa5kdb9.execute-api.ap-south-1.amazonaws.com/Tis_whatsapp_be/create_camp",
        payload
      );

      showSnackbar(
        "Campaign submitted successfully! Page will refresh...",
        "success"
      );

      // Auto-refresh after 2 seconds to show success message
      setTimeout(() => {
        window.location.reload();
      }, 100);
    } catch (err) {
      console.error(err);
      showSnackbar("Submission failed. Please try again.", "error");
      setIsSubmitting(false);
    }
  };

  const totalIncorrectNumbers =
    incorrectNumbers.length + formatInvalidNumbers.length;

  return (
    <Box
      p={isMobile ? 2 : 3}
      sx={{
        backgroundColor: "#f0f6ff",
        maxHeight: "100vh",
        overflowX: "hidden",
      }}
    >
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Grid container spacing={isMobile ? 2 : 3}>
        {/* Left Sidebar - Numbers Section */}
        <Grid item xs={12} md={3}>
          <Box
            bgcolor="#e5e1f9"
            p={1}
            borderRadius={2}
            display="flex"
            alignItems="center"
            gap={2}
          >
            <ShoppingCartIcon />
            <Box>
              <Typography fontWeight={600}>WBTN</Typography>
              <Typography>
                {whatsappBalance !== null ? whatsappBalance : "Loading..."}
              </Typography>
            </Box>
          </Box>

          <Box mt={2}>
            <Paper
              sx={{
                p: isMobile ? 1 : 2,
                height: isMobile ? "300px" : "calc(100vh - 200px)",
                maxHeight: isMobile ? "300px" : "700px",
                border: "2px solid #ccc",
                borderRadius: 2,
                overflowY: "auto",
                width: "100%",
              }}
            >
              <Box
                display="flex"
                justifyContent="space-between"
                flexWrap="wrap"
                gap={1}
                mb={2}
              >
                <Typography
                  fontWeight="bold"
                  sx={{
                    bgcolor: "#c8e6c9",
                    p: isMobile ? 1 : 1.7,
                    borderRadius: 1,
                    minWidth: "120px",
                    fontSize: isMobile ? "12px" : "14px",
                    flex: "1 1 auto",
                    textAlign: "center",
                  }}
                >
                  ✅ Correct: {correctNumbers.length}
                </Typography>

                <Typography
                  fontWeight="bold"
                  sx={{
                    bgcolor: "#ffcdd2",
                    p: isMobile ? 1 : 1,
                    borderRadius: 1,
                    minWidth: "120px",
                    fontSize: isMobile ? "12px" : "14px",
                    flex: "1 1 auto",
                    textAlign: "center",
                  }}
                >
                  ❌ Incorrect: {totalIncorrectNumbers}
                </Typography>
              </Box>

              <TextField
                multiline
                fullWidth
                minRows={isMobile ? 12 : 20}
                maxRows={isMobile ? 12 : 20}
                placeholder={`Enter phone numbers\nExample:\n9876543210\n9123456789\n+919876543210`}
                variant="outlined"
                value={numbersText}
                onChange={(e) => setNumbersText(e.target.value)}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    height: "100%",
                    alignItems: "flex-start",
                    fontSize: isMobile ? "12px" : "14px",
                  },
                }}
              />
            </Paper>

            <Box mt={2}>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    onClick={validateNumbers}
                    disabled={isSubmitting}
                    sx={{
                      height: isMobile ? "36px" : "28px",
                      fontSize: isMobile ? "12px" : "14px",
                      fontWeight: "bold",
                    }}
                  >
                    {isSubmitting ? "Validating..." : "Validate Number"}
                  </Button>
                </Grid>
                <Grid item xs={12}>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    textAlign="center"
                    sx={{ display: "block", mt: 1 }}
                  >
                    * At least one number is required to validate.
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Grid>

        {/* Right Side - Main Form - REDUCED WIDTH */}
        <Grid item xs={12} md={9}>
          <Paper
            sx={{
              p: isMobile ? 2 : 3,
              height: isMobile ? "auto" : "calc(100vh - 100px)",
              maxHeight: isMobile ? "none" : "900px",
              overflowY: "auto",
              bgcolor: "#e0f7fa",
              width: "100%",
              maxWidth: "800px", // Reduced width
              margin: "0 auto", // Center the form
            }}
          >
            {/* Message Section */}
            <Box mb={3}>
              <Typography
                mb={1}
                fontWeight="bold"
                fontSize={isMobile ? "14px" : "inherit"}
              >
                Message: {1500 - message.length} characters left
              </Typography>
              <TextareaAutosize
                minRows={isMobile ? 4 : 6}
                maxRows={isMobile ? 4 : 6}
                style={{
                  width: "100%",
                  padding: isMobile ? "10px" : "15px",
                  borderRadius: 4,
                  borderColor: "#ccc",
                  fontSize: isMobile ? "12px" : "14px",
                  fontFamily: "inherit",
                  minHeight: isMobile ? "100px" : "150px",
                  maxHeight: isMobile ? "120px" : "200px",
                }}
                placeholder="Paste Message With Emoji"
                value={message}
                onChange={handleMessageChange}
              />
            </Box>

            {/* Buttons Section */}
            <Box
              sx={{
                backgroundColor: "#f0f6ff",
                borderRadius: 2,
                p: isMobile ? 2 : 3,
                mb: 3,
                boxShadow: 1,
              }}
            >
              {/* First Row */}
              <Box sx={{ mb: 2 }}>
                <Typography
                  fontWeight="bold"
                  fontSize={isMobile ? "13px" : "inherit"}
                  mb={1}
                >
                  Call Caption & Number :
                </Typography>
                <Grid container spacing={isMobile ? 1 : 2}>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Call Now"
                      value={callButtonText}
                      onChange={(e) => setCallButtonText(e.target.value)}
                      InputProps={{
                        sx: {
                          fontSize: isMobile ? "12px" : "inherit",
                          background:
                            "linear-gradient(90deg, #e0f2f1 0%, #ffffff 120%)",
                          color: "#000",
                          "&::placeholder": {
                            color: "#000",
                            opacity: 1,
                          },
                          input: {
                            "&::placeholder": {
                              color: "#000",
                              opacity: 1,
                            },
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="+91 7898..."
                      value={callButtonNumber}
                      onChange={(e) => setCallButtonNumber(e.target.value)}
                      InputProps={{
                        sx: {
                          fontSize: isMobile ? "12px" : "inherit",
                          background:
                            "linear-gradient(90deg, #e0f2f1 0%, #ffffff 120%)",
                          color: "#000",
                          "&::placeholder": {
                            color: "#000",
                            opacity: 1,
                          },
                          input: {
                            "&::placeholder": {
                              color: "#000",
                              opacity: 1,
                            },
                          },
                        },
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>

              {/* Second Row */}
              <Box>
                <Typography
                  fontWeight="bold"
                  fontSize={isMobile ? "13px" : "inherit"}
                  mb={1}
                >
                  Link Caption & Link :
                </Typography>
                <Grid container spacing={isMobile ? 1 : 2}>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Visit Now"
                      value={linkButtonText}
                      onChange={(e) => setLinkButtonText(e.target.value)}
                      InputProps={{
                        sx: {
                          fontSize: isMobile ? "12px" : "inherit",
                          background:
                            "linear-gradient(90deg, #e0f2f1 0%, #ffffff 120%)",
                          color: "#000",
                          "&::placeholder": {
                            color: "#000",
                            opacity: 1,
                          },
                          input: {
                            "&::placeholder": {
                              color: "#000",
                              opacity: 1,
                            },
                          },
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="https://"
                      value={linkButtonUrl}
                      onChange={(e) => setLinkButtonUrl(e.target.value)}
                      InputProps={{
                        sx: {
                          fontSize: isMobile ? "12px" : "inherit",
                          background:
                            "linear-gradient(90deg, #e0f2f1 0%, #ffffff 120%)",
                          color: "#000",
                          "&::placeholder": {
                            color: "#000",
                            opacity: 1,
                          },
                          input: {
                            "&::placeholder": {
                              color: "#000",
                              opacity: 1,
                            },
                          },
                        },
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Box>

            {/* Image Upload Section */}
            <Box mb={0}>
              <Typography
                fontWeight="bold"
                fontSize={isMobile ? "14px" : "16px"}
              >
                Select and Upload Image{" "}
                <span
                  style={{
                    color: "red",
                    fontStyle: "italic",
                    fontSize: isMobile ? "12px" : "14px",
                  }}
                >
                  <span style={{ color: "red", fontStyle: "italic" }}>
                    [ max size: 1 MB ]
                  </span>{" "}
                  [ max 1 image ] {images.length >= 1 && "(Maximum reached)"}
                </span>
              </Typography>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                sx={{
                  mt: 1,
                  p: isMobile ? 2 : 3,
                  border: "2px dashed #ccc",
                  borderRadius: 2,
                  height: isMobile ? "60px" : "90px",
                  cursor: images.length < 1 ? "pointer" : "not-allowed",
                  backgroundColor: images.length >= 1 ? "#f5f5f5" : "transparent",
                  opacity: images.length >= 1 ? 0.6 : 1,
                }}
                onClick={() => images.length < 1 && document.getElementById("img-upl").click()}
                onDrop={handleImageDrop}
                onDragOver={(e) => e.preventDefault()}
              >
                <CloudUploadIcon fontSize={isMobile ? "medium" : "large"} />
                <Typography
                  ml={2}
                  variant="h6"
                  fontSize={isMobile ? "14px" : "16px"}
                >
                  {images.length < 1 ? "Click or Drop Here" : "Maximum 1 image reached"}
                </Typography>
                <input
                  id="img-upl"
                  type="file"
                  accept="image/*"
                  multiple
                  hidden
                  onChange={handleImageChange}
                />
              </Box>
              <Box mt={2} display="flex" gap={1} flexWrap="wrap">
                {images.map((img, i) => (
                  <Paper
                    key={i}
                    sx={{
                      width: isMobile ? 60 : 80,
                      height: isMobile ? 60 : 80,
                      overflow: "hidden",
                      position: "relative",
                      borderRadius: 1,
                    }}
                    variant="outlined"
                  >
                    <img
                      src={URL.createObjectURL(img)}
                      alt=""
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleImageRemove(i)}
                      sx={{ position: "absolute", top: 0, right: 0 }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Paper>
                ))}
              </Box>
            </Box>

            {/* Video and PDF Upload Section */}
            <Grid container spacing={isMobile ? 2 : 3}>
              <Grid item xs={12} md={6}>
                <Paper
                  sx={{
                    bgcolor: "#f0f6ff",
                    p: isMobile ? 1.5 : 2,
                    height: isMobile ? "100px" : "120px",
                    borderRadius: 2,
                    boxShadow: "0 4px 10px rgba(0,0,0,0.05)",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography
                    fontWeight="bold"
                    sx={{
                      color: "#0d47a1",
                      fontSize: isMobile ? "13px" : "15px",
                    }}
                  >
                    Upload Video:
                    <span style={{ color: "red", fontStyle: "italic" }}>
                      [ max size: 3 MB ]
                    </span>
                  </Typography>

                  <Box
                    display="flex"
                    alignItems="center"
                    gap={1}
                    flexWrap="wrap"
                  >
                    <Button
                      component="label"
                      startIcon={<UploadFileIcon />}
                      size="small"
                      sx={{
                        textTransform: "none",
                        bgcolor: "#ffffff",
                        border: "1px solid #90caf9",
                        fontWeight: 400,
                        fontSize: isMobile ? "11px" : "inherit",
                        "&:hover": {
                          bgcolor: "#e3f2fd",
                        },
                      }}
                    >
                      Upload Video:{" "}
                      <input
                        type="file"
                        hidden
                        accept="video/*"
                        onChange={handleVideoChange}
                      />
                    </Button>

                    {video && (
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography
                          variant="body2"
                          noWrap
                          sx={{
                            maxWidth: isMobile ? "80px" : "70px",
                            fontSize: isMobile ? "11px" : "12px",
                            color: "#2e7d32",
                          }}
                        >
                          {video.name}
                        </Typography>
                        <IconButton
                          onClick={() => setVideo(null)}
                          color="error"
                          size="small"
                          sx={{ p: "4px" }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    )}
                  </Box>
                </Paper>
              </Grid>

              {/* Upload PDF */}
              <Grid item xs={12} md={6}>
                <Paper
                  sx={{
                    bgcolor: "#f0f6ff",
                    p: isMobile ? 1.5 : 2,
                    height: isMobile ? "100px" : "120px",
                    borderRadius: 2,
                    boxShadow: "0 4px 10px rgba(0,0,0,0.05)",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography
                    fontWeight="bold"
                    sx={{
                      color: "#0d47a1",
                      fontSize: isMobile ? "13px" : "15px",
                    }}
                  >
                    Upload PDF:
                    <span style={{ color: "red", fontStyle: "italic" }}>
                      [ max size: 1 MB ]
                    </span>
                  </Typography>

                  <Box
                    display="flex"
                    alignItems="center"
                    gap={1}
                    flexWrap="wrap"
                  >
                    <Button
                      component="label"
                      startIcon={<UploadFileIcon />}
                      size="small"
                      sx={{
                        textTransform: "none",
                        bgcolor: "#ffffff",
                        border: "1px solid #90caf9",
                        fontWeight: 500,
                        fontSize: isMobile ? "11px" : "inherit",
                        "&:hover": {
                          bgcolor: "#e3f2fd",
                        },
                      }}
                    >
                      Choose PDF File
                      <input
                        type="file"
                        hidden
                        accept="application/pdf"
                        onChange={handlePdfChange}
                      />
                    </Button>

                    {pdf && (
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography
                          variant="body2"
                          noWrap
                          sx={{
                            maxWidth: isMobile ? "80px" : "50px",
                            fontSize: isMobile ? "11px" : "13px",
                            color: "#2e7d32",
                          }}
                        >
                          {pdf.name}
                        </Typography>
                        <IconButton
                          onClick={() => setPdf(null)}
                          color="error"
                          size="small"
                          sx={{ p: "4px" }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    )}
                  </Box>
                </Paper>
              </Grid>
            </Grid>

            {/* Submit Button */}
            <Grid container justifyContent="center" sx={{ marginTop: 2 }}>
              <Grid item xs={12} sm={8} md={6} lg={4}>
                <Button
                  variant="contained"
                  color="success"
                  onClick={handleSubmit}
                  fullWidth
                  disabled={isSubmitting}
                  sx={{
                    height: isMobile ? "36px" : "28px",
                    fontSize: isMobile ? "12px" : "14px",
                    fontWeight: "bold",
                  }}
                >
                  {isSubmitting ? "Submitting..." : "Submit Campaign"}
                </Button>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SmsUiUploadForm;